import React, { useState, useEffect } from 'react';
import { getProducts } from '../api';

interface DashboardProps {
  onNavigate: (page: any) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    matched: 0,
    needs_review: 0,
    no_match: 0,
    in_progress: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [all, pending, matched, review, noMatch, inProgress] = await Promise.all([
        getProducts({ page: 1, page_size: 1 }),
        getProducts({ page: 1, page_size: 1, status: 'pending' }),
        getProducts({ page: 1, page_size: 1, status: 'matched' }),
        getProducts({ page: 1, page_size: 1, status: 'needs_review' }),
        getProducts({ page: 1, page_size: 1, status: 'no_match' }),
        getProducts({ page: 1, page_size: 1, status: 'in_progress' }),
      ]);
      setStats({
        total: all.data.total,
        pending: pending.data.total,
        matched: matched.data.total,
        needs_review: review.data.total,
        no_match: noMatch.data.total,
        in_progress: inProgress.data.total,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div> Loading dashboard...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Overview of your product catalog and ASIN matching status</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card stat-card-featured">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Products</div>
        </div>
        <div className="stat-card stat-card-success">
          <div className="stat-value" style={{ color: 'var(--success)' }}>{stats.matched}</div>
          <div className="stat-label">Matched</div>
        </div>
        <div className="stat-card stat-card-warning">
          <div className="stat-value" style={{ color: 'var(--warning)' }}>{stats.needs_review}</div>
          <div className="stat-label">Needs Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--text-muted)' }}>{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card stat-card-info">
          <div className="stat-value" style={{ color: 'var(--info)' }}>{stats.in_progress}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card stat-card-danger">
          <div className="stat-value" style={{ color: 'var(--danger)' }}>{stats.no_match}</div>
          <div className="stat-label">No Match</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Quick Actions</span>
        </div>
        <div className="actions-grid">
          <button className="action-btn" onClick={() => onNavigate('search')}>
            <div className="action-icon purple">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <div className="action-text">
              <span className="action-title">Search Keepa</span>
              <span className="action-desc">Find products via Keepa API</span>
            </div>
          </button>
          <button className="action-btn" onClick={() => onNavigate('add-product')}>
            <div className="action-icon green">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            <div className="action-text">
              <span className="action-title">Add Product</span>
              <span className="action-desc">Manually add a new ASIN</span>
            </div>
          </button>
          <button className="action-btn" onClick={() => onNavigate('products')}>
            <div className="action-icon blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              </svg>
            </div>
            <div className="action-text">
              <span className="action-title">View All Products</span>
              <span className="action-desc">Browse your product catalog</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
